import { CommonModule } from '@angular/common';
import { Component, Injector, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ThreatActorEditModalComponent } from '@app/pages/incidents/details-page/threat-actor/threat-actor-edit-modal/threat-actor-edit-modal.component';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';
import { ActorTypeDescriptor } from '@app/enums/actor-type';
import { IncidentDetailsPageStore } from '../incident-details-page.store';
import { ActorEditModalData, ActorEditModalResult } from '../models/actor-edit.model';
import { FieldReferenceService } from '../field-references.service';

@Component({
  standalone: true,
  selector: 'app-threat-actor',
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule],
  templateUrl: './threat-actor.component.html',
  styleUrl: './threat-actor.component.scss',
})
export class ThreatActorComponent {
  @Input({ required: true })
  public formGroup!: FormGroup;

  public actorTypeHR: Record<string, string> = ActorTypeDescriptor;

  constructor(
    protected readonly incidentsPageStore: IncidentDetailsPageStore,
    private readonly fieldReferencesService: FieldReferenceService,
    private readonly dialog: MatDialog,
    private readonly injector: Injector,
  ) {}

  public async onRemoveActor(): Promise<void> {
    const dialogResult = await this.openConfirmationDialogAsync(
      'Remove Actor',
      'Warning:\nCurrent actor will be deleted.',
    );

    if (dialogResult) {
      this.incidentsPageStore.updateIncidentRootField('actorId', null);
      this.incidentsPageStore.updateIncidentRootField('actor', null);

      // If the actor was coming from a field reference, we need to uncheck it in the field references collection. If the actor is manual, it won't do anything.
      this.fieldReferencesService.uncheckIncidentFieldReference('ActorId');
    }
  }

  public async openActorEdit(): Promise<void> {
    const dialogRef = this.dialog.open<
      ThreatActorEditModalComponent,
      ActorEditModalData,
      ActorEditModalResult | undefined
    >(ThreatActorEditModalComponent, {
      width: '90vw',
      maxWidth: '800px',
      disableClose: true,
      injector: this.injector,
      data: {
        actor: this.incidentsPageStore.incident()!.actor,
        currentMotivation: this.incidentsPageStore.incident()!.motivation,
      },
    });

    const selectedActorResult = await firstValueFrom(dialogRef.afterClosed());
    if (selectedActorResult?.actor) {
      this.incidentsPageStore.updateIncidentRootField('actorId', selectedActorResult.actor.id);
      this.incidentsPageStore.updateIncidentRootField('actor', selectedActorResult.actor);

      // If the actor was coming from a field reference, we need to uncheck it in the field references collection. If the actor is manual, it won't do anything.
      this.fieldReferencesService.uncheckIncidentFieldReference('ActorId');

      if (selectedActorResult.motivation != null) {
        const motivationControl = this.formGroup.get('motivation');
        motivationControl?.setValue(selectedActorResult.motivation);
        motivationControl?.markAsTouched();
      }
    }
  }

  private async openConfirmationDialogAsync(title: string, content: string): Promise<boolean | undefined> {
    const dialogRef = this.dialog.open<ConfirmationModalComponent, { title: string; content: string }, boolean>(
      ConfirmationModalComponent,
      {
        width: '20vw',
        disableClose: true,
        injector: this.injector,
        data: {
          title: title,
          content: content,
        },
      },
    );

    return await firstValueFrom(dialogRef.afterClosed());
  }
}
